import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Alert, AlertDescription, AlertTitle } from "../../components/ui/alert";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Switch } from "../../components/ui/switch";
import { 
  Activity, 
  CheckCircle, 
  XCircle, 
  Warning, 
  Database, 
  Cloud, 
  ArrowClockwise,
  ShieldCheck,
  ShieldWarning,
  Clock,
  Timer,
  Flask,
  CalendarCheck,
  Receipt,
  Play,
  Pause,
  Eye,
  CreditCard,
  Envelope,
  ChatCircle,
  Info,
  CaretDown,
  CaretUp,
} from "phosphor-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

/**
 * AdminSystemHealth - Focused operator dashboard
 * 
 * Shows:
 * - Environment & deployment info
 * - Integration status (configured vs usable vs live)
 * - Scheduler control
 * - Scheduled jobs health with detail inspection
 * - Manual job runners (Appointment Reminders, Recurring Billing)
 */
export default function AdminSystemHealth() {
  const [health, setHealth] = useState(null);
  const [scheduledJobs, setScheduledJobs] = useState(null);
  const [schedulerStatus, setSchedulerStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Scheduler toggle
  const [schedulerToggling, setSchedulerToggling] = useState(false);
  
  // Job detail modal
  const [jobDetailModal, setJobDetailModal] = useState(null);
  
  // Test harness state
  const [harnessTenantId, setHarnessTenantId] = useState("");
  const [harnessDryRun, setHarnessDryRun] = useState(true);
  const [harnessAsOf, setHarnessAsOf] = useState("");
  const [harnessRunning, setHarnessRunning] = useState(null);
  const [harnessResult, setHarnessResult] = useState(null);

  const fetchHealth = async () => {
    setLoading(true);
    setError(null);
    try {
      const [healthRes, jobsRes, schedulerRes] = await Promise.all([
        fetch(`${API}/admin/system/health`, { credentials: "include" }),
        fetch(`${API}/admin/system/scheduled-jobs`, { credentials: "include" }),
        fetch(`${API}/admin/system/scheduler/status`, { credentials: "include" })
      ]);
      
      if (!healthRes.ok) {
        throw new Error(`Health HTTP ${healthRes.status}: ${healthRes.statusText}`);
      }
      const healthData = await healthRes.json();
      setHealth(healthData);
      
      if (jobsRes.ok) {
        const jobsData = await jobsRes.json();
        setScheduledJobs(jobsData);
      }
      
      if (schedulerRes.ok) {
        const schedulerData = await schedulerRes.json();
        setSchedulerStatus(schedulerData);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHealth();
  }, []);

  // Toggle scheduler
  const handleSchedulerToggle = async (enabled) => {
    setSchedulerToggling(true);
    try {
      const res = await fetch(`${API}/admin/system/scheduler/toggle`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ enabled }),
      });
      
      const data = await res.json();
      
      if (res.ok) {
        toast.success(data.message);
        // Refresh scheduler status
        const schedulerRes = await fetch(`${API}/admin/system/scheduler/status`, { credentials: "include" });
        if (schedulerRes.ok) {
          setSchedulerStatus(await schedulerRes.json());
        }
      } else {
        toast.error(data.detail || "Failed to toggle scheduler");
      }
    } catch (err) {
      toast.error("Error toggling scheduler: " + err.message);
    } finally {
      setSchedulerToggling(false);
    }
  };

  // Test harness job runner
  const runHarnessJob = async (jobType) => {
    if (!harnessTenantId) {
      toast.error("Tenant ID is required");
      return;
    }
    
    setHarnessRunning(jobType);
    setHarnessResult(null);
    
    const endpoint = jobType === "reminders" 
      ? "/admin/system/test-harness/run-appointment-reminders"
      : "/admin/system/test-harness/run-recurring-billing";
    
    try {
      const payload = {
        tenant_id: harnessTenantId,
        dry_run: harnessDryRun,
      };
      
      if (harnessAsOf) {
        payload.as_of_iso = new Date(harnessAsOf).toISOString();
      }
      
      const response = await fetch(`${API}${endpoint}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setHarnessResult(data);
        toast.success(`${jobType === "reminders" ? "Appointment Reminders" : "Recurring Billing"} job completed`);
      } else {
        setHarnessResult({ ok: false, error: data.detail || "Job failed" });
        toast.error(data.detail || "Job failed");
      }
    } catch (err) {
      setHarnessResult({ ok: false, error: err.message });
      toast.error("Failed to run job: " + err.message);
    } finally {
      setHarnessRunning(null);
    }
  };

  // Helper functions
  const getEnvBadge = (env) => {
    if (env === "production") {
      return <Badge className="bg-red-500 text-white font-bold text-sm px-3 py-1">PRODUCTION</Badge>;
    } else if (env === "staging") {
      return <Badge className="bg-amber-500 text-white font-semibold text-sm px-3 py-1">STAGING</Badge>;
    }
    return <Badge className="bg-blue-500 text-white text-sm px-3 py-1">PREVIEW</Badge>;
  };

  const getIntegrationStatus = (configured, usable, isLive = null) => {
    if (!configured) {
      return { badge: "Not Configured", color: "zinc", icon: XCircle };
    }
    if (isLive === true) {
      return { badge: "LIVE", color: "red", icon: CheckCircle };
    }
    if (usable) {
      return { badge: "Ready", color: "emerald", icon: CheckCircle };
    }
    return { badge: "Configured (Not Usable)", color: "amber", icon: Warning };
  };

  const formatTimeAgo = (isoString) => {
    if (!isoString) return "Never";
    try {
      const date = new Date(isoString);
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);
      
      if (diffMins < 1) return "Just now";
      if (diffMins < 60) return `${diffMins}m ago`;
      if (diffHours < 24) return `${diffHours}h ago`;
      return `${diffDays}d ago`;
    } catch {
      return isoString;
    }
  };

  const getJobHealthInfo = (jobName, job) => {
    if (!job) return { status: "unknown", message: "No data", severity: "zinc" };
    
    const lastRun = job.last_run ? new Date(job.last_run) : null;
    const now = new Date();
    const hoursSinceRun = lastRun ? (now - lastRun) / 3600000 : Infinity;
    
    // Define expected cadences
    const cadences = {
      appointment_reminders: { maxHours: 2, label: "Hourly" },
      daily_billing: { maxHours: 25, label: "Daily" },
      overdue_invoices: { maxHours: 25, label: "Daily" },
      weekly_summary: { maxHours: 168, label: "Weekly" },
      scheduled_campaigns: { maxHours: 0.5, label: "Every minute" },
      nightly_backup: { maxHours: 25, label: "Nightly" },
      invoice_followup_scheduler: { maxHours: 7, label: "Every 6h" },
      followup_dispatcher: { maxHours: 0.2, label: "Every 5min" },
      retry_dispatcher: { maxHours: 0.25, label: "Every 10min" },
      voice_notes_cleanup: { maxHours: 25, label: "Nightly" },
      ops_alerting: { maxHours: 0.2, label: "Every 5min" },
    };
    
    const cadence = cadences[jobName] || { maxHours: 24, label: "Unknown" };
    
    if (job.last_status === "failed") {
      return { 
        status: "failed", 
        message: job.last_error || "Last run failed",
        severity: "red",
        cadence: cadence.label
      };
    }
    
    if (job.last_status === "running") {
      return { 
        status: "running", 
        message: "Currently running",
        severity: "blue",
        cadence: cadence.label
      };
    }
    
    if (!lastRun) {
      return { 
        status: "never_run", 
        message: "Never executed",
        severity: "zinc",
        cadence: cadence.label
      };
    }
    
    if (hoursSinceRun > cadence.maxHours * 2) {
      return { 
        status: "overdue", 
        message: `Overdue (${formatTimeAgo(job.last_run)})`,
        severity: "red",
        cadence: cadence.label
      };
    }
    
    if (hoursSinceRun > cadence.maxHours) {
      return { 
        status: "late", 
        message: `Late (${formatTimeAgo(job.last_run)})`,
        severity: "amber",
        cadence: cadence.label
      };
    }
    
    return { 
      status: "healthy", 
      message: `OK (${formatTimeAgo(job.last_run)})`,
      severity: "emerald",
      cadence: cadence.label
    };
  };

  // Loading state
  if (loading) {
    return (
      <div className="p-6 space-y-6">
        <div className="flex items-center gap-3">
          <Activity size={28} className="text-[#FF6600] animate-pulse" />
          <h1 className="text-2xl font-heading uppercase">System Health</h1>
        </div>
        <Card className="bg-zinc-900 border-zinc-800">
          <CardContent className="p-12 text-center">
            <ArrowClockwise size={32} className="animate-spin text-zinc-400 mx-auto mb-4" />
            <p className="text-zinc-400">Loading system health...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="p-6 space-y-6">
        <div className="flex items-center gap-3">
          <Activity size={28} className="text-[#FF6600]" />
          <h1 className="text-2xl font-heading uppercase">System Health</h1>
        </div>
        <Alert variant="destructive" className="bg-red-500/10 border-red-500/30">
          <Warning size={20} />
          <AlertTitle>Failed to Load Health Data</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
        <Button onClick={fetchHealth} variant="outline" className="border-2">
          <ArrowClockwise size={18} className="mr-2" />
          Retry
        </Button>
      </div>
    );
  }

  const isProd = health?.app_env === "production";

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Activity size={28} className="text-[#FF6600]" />
          <h1 className="text-2xl font-heading uppercase">System Health</h1>
        </div>
        <Button onClick={fetchHealth} variant="outline" size="sm" className="border-2">
          <ArrowClockwise size={16} className="mr-2" />
          Refresh
        </Button>
      </div>

      {/* Critical Warning Banner */}
      {health && !health.db_marker_ok && (
        <Alert className="bg-red-500/20 border-red-500 text-red-200">
          <ShieldWarning size={24} className="text-red-400" />
          <AlertTitle className="text-red-300 font-bold text-lg">
            CRITICAL: Database Environment Mismatch
          </AlertTitle>
          <AlertDescription className="text-red-200">
            The database environment marker does not match the application environment.
            <strong className="block mt-2">Do not proceed with any destructive operations.</strong>
          </AlertDescription>
        </Alert>
      )}

      {/* ================================================================ */}
      {/* ENVIRONMENT & DEPLOYMENT STATUS */}
      {/* ================================================================ */}
      <Card className="bg-zinc-900 border-zinc-800">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <ShieldCheck size={22} className="text-[#FF6600]" />
            Environment Status
          </CardTitle>
          <CardDescription>Deployment environment and system state</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Deployment Environment */}
            <div className="p-4 bg-zinc-800/50 rounded-lg">
              <span className="text-zinc-400 text-sm block mb-2">Deployment Environment</span>
              <div className="flex items-center gap-2">
                {getEnvBadge(health?.app_env)}
              </div>
            </div>
            
            {/* Database Marker */}
            <div className="p-4 bg-zinc-800/50 rounded-lg">
              <span className="text-zinc-400 text-sm block mb-2">Database Marker</span>
              <div className="flex items-center gap-2">
                {health?.db_marker_ok ? (
                  <>
                    <CheckCircle size={20} weight="fill" className="text-emerald-500" />
                    <span className="text-emerald-400">Match</span>
                  </>
                ) : (
                  <>
                    <XCircle size={20} weight="fill" className="text-red-500" />
                    <span className="text-red-400">Mismatch</span>
                  </>
                )}
              </div>
            </div>
            
            {/* Effective Payments Mode */}
            <div className="p-4 bg-zinc-800/50 rounded-lg">
              <span className="text-zinc-400 text-sm block mb-2">Payments Mode</span>
              <div className="flex items-center gap-2">
                {health?.finix_health?.effective_environment === "production" ? (
                  <Badge className="bg-red-500 text-white font-bold">LIVE</Badge>
                ) : health?.finix_health?.effective_environment === "sandbox" ? (
                  <Badge className="bg-amber-500 text-white">SANDBOX</Badge>
                ) : (
                  <Badge className="bg-zinc-600 text-white">DISABLED</Badge>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ================================================================ */}
      {/* SCHEDULER CONTROL */}
      {/* ================================================================ */}
      <Card className="bg-zinc-900 border-zinc-800">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Timer size={22} className="text-[#FF6600]" />
            Scheduler Control
          </CardTitle>
          <CardDescription>Background job scheduler state and control</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-zinc-800/50 rounded-lg">
            <div className="flex items-center gap-4">
              <div>
                <span className="text-zinc-300 font-medium block">Scheduler Status</span>
                <span className="text-zinc-500 text-sm">{schedulerStatus?.reason || "Loading..."}</span>
              </div>
            </div>
            <div className="flex items-center gap-4">
              {schedulerStatus?.enabled ? (
                <Badge className="bg-emerald-500 text-white flex items-center gap-1">
                  <Play size={14} weight="fill" />
                  RUNNING
                </Badge>
              ) : (
                <Badge className="bg-zinc-600 text-white flex items-center gap-1">
                  <Pause size={14} weight="fill" />
                  PAUSED
                </Badge>
              )}
              
              {/* Toggle (non-production only) */}
              {schedulerStatus?.can_toggle && (
                <div className="flex items-center gap-2">
                  <Switch
                    checked={schedulerStatus?.enabled || false}
                    onCheckedChange={handleSchedulerToggle}
                    disabled={schedulerToggling}
                    data-testid="scheduler-toggle"
                  />
                  <span className="text-zinc-400 text-sm">
                    {schedulerToggling ? "..." : (schedulerStatus?.enabled ? "On" : "Off")}
                  </span>
                </div>
              )}
            </div>
          </div>
          
          {/* Production guardrail notice */}
          {isProd && (
            <Alert className="bg-zinc-800/50 border-zinc-700">
              <Info size={18} className="text-zinc-400" />
              <AlertDescription className="text-zinc-400 text-sm">
                Scheduler cannot be disabled in production. This is a safety guardrail.
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* ================================================================ */}
      {/* INTEGRATION STATUS */}
      {/* ================================================================ */}
      <Card className="bg-zinc-900 border-zinc-800">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Cloud size={22} className="text-[#FF6600]" />
            Integration Status
          </CardTitle>
          <CardDescription>External service connectivity and mode</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Payments (Finix) */}
            <div className={`p-4 rounded-lg border-2 ${
              health?.finix_health?.effective_environment === "production"
                ? "bg-red-500/10 border-red-500/30"
                : health?.finix_health?.tokenization_ready
                  ? "bg-emerald-500/10 border-emerald-500/30"
                  : "bg-zinc-800/50 border-zinc-700"
            }`}>
              <div className="flex items-center gap-3 mb-2">
                <CreditCard size={22} className={
                  health?.finix_health?.effective_environment === "production" 
                    ? "text-red-500" 
                    : health?.finix_health?.tokenization_ready 
                      ? "text-emerald-500" 
                      : "text-zinc-400"
                } />
                <span className="font-medium text-white">Payments (Finix)</span>
              </div>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between">
                  <span className="text-zinc-400">Environment:</span>
                  <span className={
                    health?.finix_health?.effective_environment === "production" 
                      ? "text-red-400 font-bold" 
                      : "text-amber-400"
                  }>
                    {health?.finix_health?.effective_environment?.toUpperCase() || "—"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-zinc-400">Tokenization:</span>
                  <span className={health?.finix_health?.tokenization_ready ? "text-emerald-400" : "text-zinc-500"}>
                    {health?.finix_health?.tokenization_ready ? "Ready" : "Not Ready"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-zinc-400">Sandbox Config:</span>
                  <span className={health?.finix_health?.sandbox_configured ? "text-emerald-400" : "text-zinc-500"}>
                    {health?.finix_health?.sandbox_configured ? "✓" : "—"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-zinc-400">Production Config:</span>
                  <span className={health?.finix_health?.production_configured ? "text-emerald-400" : "text-zinc-500"}>
                    {health?.finix_health?.production_configured ? "✓" : "—"}
                  </span>
                </div>
              </div>
            </div>

            {/* Email (SendGrid) */}
            {(() => {
              const sendgrid = health?.comms?.sendgrid || {};
              const status = getIntegrationStatus(sendgrid.configured, sendgrid.usable);
              return (
                <div className={`p-4 rounded-lg border-2 ${
                  sendgrid.usable 
                    ? "bg-emerald-500/10 border-emerald-500/30" 
                    : "bg-zinc-800/50 border-zinc-700"
                }`}>
                  <div className="flex items-center gap-3 mb-2">
                    <Envelope size={22} className={sendgrid.usable ? "text-emerald-500" : "text-zinc-400"} />
                    <span className="font-medium text-white">Email (SendGrid)</span>
                  </div>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span className="text-zinc-400">Status:</span>
                      <Badge className={`bg-${status.color}-500/20 text-${status.color}-400 border border-${status.color}-500/30`}>
                        {status.badge}
                      </Badge>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-zinc-400">Mode:</span>
                      <span className={sendgrid.environment === "production" ? "text-emerald-400" : "text-amber-400"}>
                        {sendgrid.environment || "—"}
                      </span>
                    </div>
                    {sendgrid.reason && (
                      <div className="flex justify-between">
                        <span className="text-zinc-400">Note:</span>
                        <span className="text-zinc-500 text-xs">{sendgrid.reason}</span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}

            {/* SMS (Twilio) */}
            {(() => {
              const twilio = health?.comms?.twilio || {};
              const status = getIntegrationStatus(twilio.configured, twilio.usable);
              return (
                <div className={`p-4 rounded-lg border-2 ${
                  twilio.usable 
                    ? "bg-emerald-500/10 border-emerald-500/30" 
                    : "bg-zinc-800/50 border-zinc-700"
                }`}>
                  <div className="flex items-center gap-3 mb-2">
                    <ChatCircle size={22} className={twilio.usable ? "text-emerald-500" : "text-zinc-400"} />
                    <span className="font-medium text-white">SMS (Twilio)</span>
                  </div>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span className="text-zinc-400">Status:</span>
                      <Badge className={`bg-${status.color}-500/20 text-${status.color}-400 border border-${status.color}-500/30`}>
                        {status.badge}
                      </Badge>
                    </div>
                    {twilio.preview_allowlist_count !== null && (
                      <div className="flex justify-between">
                        <span className="text-zinc-400">Allowlist:</span>
                        <span className="text-zinc-300">{twilio.preview_allowlist_count} numbers</span>
                      </div>
                    )}
                    {twilio.reason && (
                      <div className="flex justify-between">
                        <span className="text-zinc-400">Note:</span>
                        <span className="text-zinc-500 text-xs">{twilio.reason}</span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}

            {/* R2 Storage */}
            {(() => {
              const storage = health?.storage || {};
              const status = getIntegrationStatus(storage.configured, storage.usable);
              return (
                <div className={`p-4 rounded-lg border-2 ${
                  storage.usable 
                    ? "bg-emerald-500/10 border-emerald-500/30" 
                    : "bg-zinc-800/50 border-zinc-700"
                }`}>
                  <div className="flex items-center gap-3 mb-2">
                    <Database size={22} className={storage.usable ? "text-emerald-500" : "text-zinc-400"} />
                    <span className="font-medium text-white">R2 Storage</span>
                  </div>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span className="text-zinc-400">Status:</span>
                      <Badge className={`bg-${status.color}-500/20 text-${status.color}-400 border border-${status.color}-500/30`}>
                        {status.badge}
                      </Badge>
                    </div>
                    {storage.latency_ms && (
                      <div className="flex justify-between">
                        <span className="text-zinc-400">Latency:</span>
                        <span className="text-emerald-400">{storage.latency_ms}ms</span>
                      </div>
                    )}
                    {storage.reason && (
                      <div className="flex justify-between">
                        <span className="text-zinc-400">Note:</span>
                        <span className="text-zinc-500 text-xs">{storage.reason}</span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}
          </div>
        </CardContent>
      </Card>

      {/* ================================================================ */}
      {/* SCHEDULED JOBS HEALTH */}
      {/* ================================================================ */}
      <Card className="bg-zinc-900 border-zinc-800">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Clock size={22} className="text-[#FF6600]" />
            Scheduled Jobs Health
          </CardTitle>
          <CardDescription>Background job execution status (click job for details)</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Overall Status */}
          <div className="flex items-center justify-between p-4 bg-zinc-800/50 rounded-lg">
            <span className="text-zinc-300 font-medium">Overall Status</span>
            {scheduledJobs?.status === "healthy" ? (
              <Badge className="bg-emerald-500 text-white">HEALTHY</Badge>
            ) : scheduledJobs?.status === "warning" ? (
              <Badge className="bg-amber-500 text-white">WARNING</Badge>
            ) : scheduledJobs?.status === "degraded" ? (
              <Badge className="bg-red-500 text-white">DEGRADED</Badge>
            ) : (
              <Badge className="bg-zinc-600 text-white">UNKNOWN</Badge>
            )}
          </div>

          {/* Health Issues */}
          {scheduledJobs?.health_issues?.length > 0 && (
            <Alert className="bg-amber-500/10 border-amber-500/30">
              <Warning size={20} className="text-amber-400" />
              <AlertTitle className="text-amber-300">Issues Detected</AlertTitle>
              <AlertDescription className="text-amber-200">
                <ul className="list-disc list-inside mt-2 space-y-1">
                  {scheduledJobs.health_issues.map((issue, i) => (
                    <li key={i} className="text-sm">
                      <strong>{issue.job}</strong>: {issue.issue}
                      {issue.error && <span className="text-red-300"> - {issue.error?.substring(0, 50)}</span>}
                    </li>
                  ))}
                </ul>
              </AlertDescription>
            </Alert>
          )}

          {/* Jobs Grid */}
          {scheduledJobs?.jobs && Object.keys(scheduledJobs.jobs).length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {Object.entries(scheduledJobs.jobs).map(([jobName, job]) => {
                const info = getJobHealthInfo(jobName, job);
                return (
                  <button
                    key={jobName}
                    onClick={() => setJobDetailModal({ name: jobName, ...job, healthInfo: info })}
                    className={`p-3 rounded-lg border text-left transition-all hover:scale-[1.02] cursor-pointer ${
                      info.severity === "emerald" ? "bg-emerald-500/10 border-emerald-500/30 hover:bg-emerald-500/20" :
                      info.severity === "amber" ? "bg-amber-500/10 border-amber-500/30 hover:bg-amber-500/20" :
                      info.severity === "red" ? "bg-red-500/10 border-red-500/30 hover:bg-red-500/20" :
                      info.severity === "blue" ? "bg-blue-500/10 border-blue-500/30 hover:bg-blue-500/20" :
                      "bg-zinc-800/50 border-zinc-700 hover:bg-zinc-800"
                    }`}
                    data-testid={`job-${jobName}`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-white text-sm font-medium truncate">{jobName}</span>
                      <Eye size={14} className="text-zinc-500" />
                    </div>
                    <div className={`text-xs ${
                      info.severity === "emerald" ? "text-emerald-400" :
                      info.severity === "amber" ? "text-amber-400" :
                      info.severity === "red" ? "text-red-400" :
                      info.severity === "blue" ? "text-blue-400" :
                      "text-zinc-500"
                    }`}>
                      {info.message}
                    </div>
                    <div className="text-xs text-zinc-500 mt-1">{info.cadence}</div>
                  </button>
                );
              })}
            </div>
          )}

          {/* No jobs */}
          {(!scheduledJobs?.jobs || Object.keys(scheduledJobs.jobs).length === 0) && (
            <div className="text-center py-6 text-zinc-500">
              <Clock size={32} className="mx-auto mb-2 opacity-50" />
              <p>No scheduled job runs recorded yet</p>
            </div>
          )}

          {/* Last checked */}
          {scheduledJobs?.checked_at && (
            <p className="text-xs text-zinc-500 text-right">
              Last checked: {new Date(scheduledJobs.checked_at).toLocaleString()}
            </p>
          )}
        </CardContent>
      </Card>

      {/* ================================================================ */}
      {/* MANUAL JOB RUNNERS (APPOINTMENT REMINDERS & RECURRING BILLING) */}
      {/* ================================================================ */}
      <Card className="bg-zinc-900 border-zinc-800 border-2 border-dashed border-amber-500/50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Flask size={22} className="text-amber-500" />
            Manual Job Runners
            {isProd && (
              <Badge className="bg-red-500/20 text-red-400 border border-red-500/30 ml-2">
                Production
              </Badge>
            )}
          </CardTitle>
          <CardDescription>
            Trigger appointment reminders or recurring billing for a specific tenant.
            {isProd && " Use with caution in production."}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Input Fields */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label htmlFor="harness-tenant" className="text-zinc-400 text-sm">
                Tenant ID *
              </Label>
              <Input
                id="harness-tenant"
                value={harnessTenantId}
                onChange={(e) => setHarnessTenantId(e.target.value)}
                placeholder="tenant_xxx"
                className="bg-zinc-800 border-zinc-700 text-white mt-1"
                data-testid="harness-tenant-input"
              />
            </div>
            <div>
              <Label htmlFor="harness-asof" className="text-zinc-400 text-sm">
                As-Of DateTime (optional)
              </Label>
              <Input
                id="harness-asof"
                type="datetime-local"
                value={harnessAsOf}
                onChange={(e) => setHarnessAsOf(e.target.value)}
                className="bg-zinc-800 border-zinc-700 text-white mt-1"
                data-testid="harness-asof-input"
              />
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={harnessDryRun}
                  onChange={(e) => setHarnessDryRun(e.target.checked)}
                  className="w-4 h-4 rounded border-zinc-600 bg-zinc-800 text-amber-500"
                  data-testid="harness-dry-run-checkbox"
                />
                <span className="text-zinc-300 text-sm">Dry Run (preview only)</span>
              </label>
            </div>
          </div>
          
          {/* Action Buttons */}
          <div className="flex gap-3 pt-2">
            <Button
              onClick={() => runHarnessJob("reminders")}
              disabled={!harnessTenantId || harnessRunning}
              className="bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="run-reminders-btn"
            >
              {harnessRunning === "reminders" ? (
                <ArrowClockwise size={18} className="mr-2 animate-spin" />
              ) : (
                <CalendarCheck size={18} className="mr-2" />
              )}
              Run Appointment Reminders
            </Button>
            <Button
              onClick={() => runHarnessJob("billing")}
              disabled={!harnessTenantId || harnessRunning}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="run-billing-btn"
            >
              {harnessRunning === "billing" ? (
                <ArrowClockwise size={18} className="mr-2 animate-spin" />
              ) : (
                <Receipt size={18} className="mr-2" />
              )}
              Run Recurring Billing
            </Button>
          </div>
          
          {/* Results Panel */}
          {harnessResult && (
            <div className={`p-4 rounded-lg border ${
              harnessResult.ok 
                ? "bg-emerald-500/10 border-emerald-500/30" 
                : "bg-red-500/10 border-red-500/30"
            }`}>
              <div className="flex items-center gap-2 mb-2">
                {harnessResult.ok ? (
                  <CheckCircle size={20} weight="fill" className="text-emerald-500" />
                ) : (
                  <XCircle size={20} weight="fill" className="text-red-500" />
                )}
                <span className={`font-semibold ${harnessResult.ok ? "text-emerald-400" : "text-red-400"}`}>
                  {harnessResult.ok ? "Job Completed" : "Job Failed"}
                </span>
                {harnessResult.dry_run && (
                  <Badge className="bg-amber-500/20 text-amber-400 ml-2">DRY RUN</Badge>
                )}
              </div>
              <pre className="text-xs text-zinc-400 bg-zinc-950 p-3 rounded overflow-x-auto mt-2">
                {JSON.stringify(harnessResult, null, 2)}
              </pre>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ================================================================ */}
      {/* JOB DETAIL MODAL */}
      {/* ================================================================ */}
      <Dialog open={!!jobDetailModal} onOpenChange={() => setJobDetailModal(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-700 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Clock size={20} className="text-[#FF6600]" />
              Job: {jobDetailModal?.name}
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              {jobDetailModal?.healthInfo?.cadence || "Unknown schedule"}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Status */}
            <div className="grid grid-cols-2 gap-4">
              <div className="p-3 bg-zinc-800/50 rounded-lg">
                <span className="text-zinc-500 text-xs block mb-1">Status</span>
                <Badge className={`bg-${jobDetailModal?.healthInfo?.severity || "zinc"}-500/20 text-${jobDetailModal?.healthInfo?.severity || "zinc"}-400`}>
                  {jobDetailModal?.last_status || "unknown"}
                </Badge>
              </div>
              <div className="p-3 bg-zinc-800/50 rounded-lg">
                <span className="text-zinc-500 text-xs block mb-1">Health</span>
                <span className={`text-${jobDetailModal?.healthInfo?.severity || "zinc"}-400 text-sm`}>
                  {jobDetailModal?.healthInfo?.message}
                </span>
              </div>
            </div>
            
            {/* Timing */}
            <div className="grid grid-cols-2 gap-4">
              <div className="p-3 bg-zinc-800/50 rounded-lg">
                <span className="text-zinc-500 text-xs block mb-1">Last Run</span>
                <span className="text-white text-sm">
                  {jobDetailModal?.last_run 
                    ? new Date(jobDetailModal.last_run).toLocaleString() 
                    : "Never"}
                </span>
              </div>
              <div className="p-3 bg-zinc-800/50 rounded-lg">
                <span className="text-zinc-500 text-xs block mb-1">Duration</span>
                <span className="text-white text-sm">
                  {jobDetailModal?.duration_seconds != null
                    ? `${(jobDetailModal.duration_seconds * 1000).toFixed(0)} ms`
                    : "—"}
                </span>
              </div>
            </div>
            
            {/* Period Key */}
            {jobDetailModal?.last_period_key && (
              <div className="p-3 bg-zinc-800/50 rounded-lg">
                <span className="text-zinc-500 text-xs block mb-1">Period Key</span>
                <span className="text-white text-sm font-mono">{jobDetailModal.last_period_key}</span>
              </div>
            )}
            
            {/* Error */}
            {jobDetailModal?.last_error && (
              <Alert className="bg-red-500/10 border-red-500/30">
                <Warning size={18} className="text-red-400" />
                <AlertTitle className="text-red-300 text-sm">Last Error</AlertTitle>
                <AlertDescription className="text-red-200 text-xs font-mono mt-1">
                  {jobDetailModal.last_error}
                </AlertDescription>
              </Alert>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setJobDetailModal(null)}
              className="border-zinc-600 text-zinc-300"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
